import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Box, Button, Callout, Dialog, Flex, Switch, Text, TextField } from '@radix-ui/themes';
import { getGuestMenuSettings, saveGuestMenuSettings } from '../../api/QrOrderingApi';
import { ChoiceSelect, Field } from './StaffOrderingUI';

export default function OrderAvailability({locationId}) {
  const client=useQueryClient();
  const query=useQuery({queryKey:['ordering-settings',locationId],queryFn:async()=>(await getGuestMenuSettings(locationId)).data,refetchInterval:15000,retry:false});
  const [dialog,setDialog]=useState(false);
  const [duration,setDuration]=useState('30');
  const [message,setMessage]=useState('');
  const [saving,setSaving]=useState(false);
  const [error,setError]=useState('');
  const [now,setNow]=useState(Date.now);
  useEffect(()=>{const timer=setInterval(()=>setNow(Date.now()),1000);return()=>clearInterval(timer);},[]);
  const until=query.data?.pause_until?new Date(query.data.pause_until):null;
  const open=Boolean(query.data&&(query.data.ordering_open||(until&&until.getTime()<=now)));
  const showDialog=()=>{setMessage(query.data?.paused_message||'Online ordering is paused. Please order with our team.');setError('');setDialog(true);};
  const save=async data=>{
    setSaving(true);setError('');
    try{
      const response=await saveGuestMenuSettings(locationId,data);
      client.setQueryData(['ordering-settings',locationId],response.data);
      client.invalidateQueries({queryKey:['guest-menu']});
      setDialog(false);
    }catch(failure){setError(failure.response?.data?.detail||'Could not change ordering availability. Please try again.');}
    finally{setSaving(false);}
  };
  return <Box>
    <Flex gap="3" align="center" wrap="wrap">
      <Text as="label" htmlFor={`accept-orders-${locationId}`} size="2" weight="medium">Accept orders</Text>
      <Switch id={`accept-orders-${locationId}`} aria-label="Accept orders" checked={open} disabled={!query.data||query.isError||saving} onCheckedChange={value=>value?save({ordering_open:true}):showDialog()}/>
      <Text size="1" color={open?'green':'amber'}>{query.isPending?'Checking availability…':query.isError?'Availability unavailable':open?'Ordering open':until?`Paused until ${until.toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'})}`:'Paused until resumed'}</Text>
      {!open&&query.data&&!query.isError&&<Button size="1" variant="ghost" onClick={showDialog} disabled={saving}>Change pause</Button>}
      {query.isError&&<Button size="1" variant="ghost" onClick={()=>query.refetch()}>Retry</Button>}
    </Flex>
    {error&&!dialog&&<Text role="alert" color="red" size="2">{error}</Text>}
    <Dialog.Root open={dialog} onOpenChange={value=>{if(!saving)setDialog(value);}}><Dialog.Content maxWidth="440px">
      <Dialog.Title>Pause online ordering</Dialog.Title><Dialog.Description>Guests can still browse the menu. Orders already sent are unaffected.</Dialog.Description>
      <form onSubmit={event=>{event.preventDefault();save({ordering_open:false,paused_message:message.trim()||'Online ordering is paused. Please order with our team.',...(duration==='manual'?{}:{pause_minutes:Number(duration)})});}}>
        <Flex direction="column" gap="4" mt="4">
          <Field label="Pause for"><ChoiceSelect label="Pause duration" value={duration} onChange={setDuration} disabled={saving} options={[{value:'15',label:'15 minutes'},{value:'30',label:'30 minutes'},{value:'60',label:'60 minutes'},{value:'manual',label:'Until I resume ordering'}]}/></Field>
          <Field label="Message shown to guests"><TextField.Root aria-label="Pause message" value={message} maxLength={250} disabled={saving} onChange={event=>setMessage(event.target.value)}/></Field>
          {error&&<Callout.Root role="alert" color="red"><Callout.Text>{error}</Callout.Text></Callout.Root>}
          <Flex justify="end" gap="3"><Button type="button" variant="soft" color="gray" disabled={saving} onClick={()=>setDialog(false)}>Cancel</Button><Button type="submit" disabled={saving}>{saving?'Saving…':'Pause orders'}</Button></Flex>
        </Flex>
      </form>
    </Dialog.Content></Dialog.Root>
  </Box>;
}
